import { useCallback, useEffect, useRef, useState } from 'react';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { useFirebase } from './useFirebase';

interface UseSseConnectOptions<T> {
  shouldCloseConnection?: (data: T) => boolean;
  onError?: (error: any) => void;
  getTokenParamName?: string;
}

export const useSseConnect = <T = any>(options?: UseSseConnectOptions<T>) => {
  const [isSseConnected, setIsSseConnected] = useState<boolean>(false);
  const evtSourceRef = useRef<EventSource | null>(null);
  const { getToken } = useFirebase();
  const baseUrl = import.meta.env.VITE_FINANCING_API_BASE_URL;

  const { shouldCloseConnection = () => false, onError } = options || {};

  const TOKEN_KEY = 'sseToken';
  const TOKEN_EXPIRY_MS = 15 * 60 * 1000;

  const saveSeeToken = (token: string) => {
    const record = {
      value: token,
      expiry: Date.now() + TOKEN_EXPIRY_MS,
    };
    sessionStorage.setItem(TOKEN_KEY, JSON.stringify(record));
  };

  const getValidSeeToken = (): string | null => {
    const raw = sessionStorage.getItem(TOKEN_KEY);
    if (!raw) return null;

    try {
      const record = JSON.parse(raw);
      if (Date.now() > record.expiry) {
        sessionStorage.removeItem(TOKEN_KEY);
        return null;
      }
      return record.value;
    } catch {
      sessionStorage.removeItem(TOKEN_KEY);
      return null;
    }
  };

  const removeSeeToken = () => {
    sessionStorage.removeItem(TOKEN_KEY);
  };

  const startSse = useCallback(
    async (url: string, onMessage: (data: T) => void) => {
      if (evtSourceRef.current) {
        evtSourceRef.current.close();
      }

      try {
        const token = await getToken();
        if (!token) {
          showErrorDialog('Error', 'Authentication token not found');
          return;
        }

        url = `${baseUrl}${url}&token=${token}`;
        const evtSource = new EventSource(url);
        evtSourceRef.current = evtSource;

        evtSource.onmessage = (event) => {
          try {
            const data: T = JSON.parse(event.data);
            onMessage(data);

            if (shouldCloseConnection(data)) {
              evtSource.close();
              evtSourceRef.current = null;
              setIsSseConnected(false);
            }
          } catch (parseError) {
            console.error('Failed to parse message:', parseError);
          }
        };

        evtSource.onerror = (err: Event) => {
          console.error('Error:', err);
          if (onError) onError(err);
          else showErrorDialog('Error', 'An error occurred. Please try again.');

          evtSource.close();
          evtSourceRef.current = null;
          setIsSseConnected(false);
        };

        setIsSseConnected(true);
      } catch (error) {
        sessionStorage.removeItem(TOKEN_KEY);
        showErrorDialog('Error', 'Authentication failed. Please try again.');
        setIsSseConnected(false);
      }
    },
    [getToken, shouldCloseConnection, onError],
  );

  const reconnect = useCallback(
    (urlBase: string, onComplete: (data: any) => void) => {
      const savedToken = getValidSeeToken();
      if (savedToken) {
        startSse(urlBase, onComplete);
      }
    },
    [startSse],
  );

  useEffect(() => {
    return () => {
      if (evtSourceRef.current) {
        evtSourceRef.current.close();
        evtSourceRef.current = null;
      }
    };
  }, []);

  return { startSse, reconnect, isSseConnected, setIsSseConnected, saveSeeToken, getValidSeeToken, removeSeeToken };
};
